import { AlertIcon } from '@approvalmax/ui';
import { Alert, Flex, Popup, Text } from '@approvalmax/ui/src/components';
import { selectors } from 'modules/common';
import { stateTree } from 'modules/data';
import { memo, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { OffboardUserPopupData } from '../../reducers/page/activePopupReducer';
import { getActivePopup } from '../../selectors/pageSelectors';
import ApprovalWorkflows from './components/ApprovalWorkflows/ApprovalWorkflows';
import PopupButtonsStep1 from './components/PopupButtonsStep1/PopupButtonsStep1';
import PopupButtonsStep2 from './components/PopupButtonsStep2/PopupButtonsStep2';
import { Requests } from './components/Requests/Requests';
import { OffboardingProvider } from './OffboardingContext';
import { useOffboardUserMutation, useOffboardUserQuery } from './OffboardUserPopupContent.hooks';
import { messages } from './OffboardUserPopupContent.messages';
import { OffbardingStep } from './OffboardUserPopupContent.types';

const OffboardUserPopupBody = memo(() => {
    const offboardingUser = useSelector((state: stateTree.State) => {
        const email = getActivePopup<OffboardUserPopupData>(state).userEmail;

        return selectors.user.getUserById(state, email);
    });
    const [step, setStep] = useState(OffbardingStep.First);
    const companyId = useSelector(selectors.navigation.getActiveCompanyId)!;

    const showSecondStep = useCallback(() => {
        setStep(OffbardingStep.Second);
    }, []);

    const {
        isInitialLoading: offboardUserIsInitialLoading,
        data: offboardUserData,
        isFetching: offboardUserIsFetching,
        isError: offboardUserIsError,
    } = useOffboardUserQuery();

    const { mutate: offboardUserMutate, isLoading: offboardUserIsLoading } = useOffboardUserMutation(
        offboardingUser!.databaseId!,
        offboardUserData!,
        companyId,
        Boolean(offboardUserData.isWorkflowManager)
    );

    const company = useSelector((state: stateTree.State) => selectors.company.getCompanyById(state, companyId!));

    const userHasDelegates = useMemo(
        () => company.delegates.find((delegate) => delegate.userId === offboardingUser?.id),
        [company.delegates, offboardingUser?.id]
    );

    const onBackButtonClick = useCallback(() => {
        setStep(OffbardingStep.First);
    }, []);

    return (
        <>
            <Popup.Header
                title={messages.offBoardPopupTitle({ displayName: offboardingUser?.displayName })}
                postfix={messages.offBoardPopupStepTitle({ stepNumber: step })}
                actions={
                    step === OffbardingStep.First ? (
                        <PopupButtonsStep1
                            showSecondStep={showSecondStep}
                            offboardUserData={offboardUserData}
                            isLoading={
                                offboardUserIsInitialLoading ||
                                offboardUserIsFetching ||
                                offboardUserIsLoading ||
                                offboardUserIsError
                            }
                        />
                    ) : (
                        <PopupButtonsStep2
                            offboardUserMutate={offboardUserMutate}
                            offboardUserData={offboardUserData}
                            isLoading={offboardUserIsInitialLoading || offboardUserIsFetching || offboardUserIsLoading}
                            back={onBackButtonClick}
                        />
                    )
                }
            />

            <Popup.Body className='fs-mask'>
                <Flex direction='column' spacing='16'>
                    {userHasDelegates && (
                        <Alert startIcon={<AlertIcon />} size='small' color='midnight20' closable>
                            {messages.delegatesWarningText({ displayName: offboardingUser?.displayName })}
                        </Alert>
                    )}

                    <Text font='body' fontWeight='medium'>
                        {step === OffbardingStep.First
                            ? messages.instructionForWorkflow({ br: <br />, displayName: offboardingUser?.displayName })
                            : messages.instructionForReplacement({
                                  br: <br />,
                                  displayName: offboardingUser?.displayName,
                              })}
                    </Text>

                    {step === OffbardingStep.First ? (
                        <ApprovalWorkflows
                            disabled={offboardUserIsLoading}
                            isLoading={offboardUserIsInitialLoading || offboardUserIsFetching}
                            workflows={offboardUserData?.workflows}
                            companyId={companyId}
                        />
                    ) : (
                        <Requests
                            disabled={offboardUserIsLoading}
                            isLoading={offboardUserIsInitialLoading || offboardUserIsFetching}
                            requests={offboardUserData?.requests}
                            displayName={offboardingUser?.displayName}
                            companyId={companyId}
                            quantityRequests={offboardUserData.quantityRequests}
                            isWorkflowManager={offboardUserData.isWorkflowManager}
                        />
                    )}
                </Flex>
            </Popup.Body>
        </>
    );
});

OffboardUserPopupBody.displayName = 'OffboardUserPopupBody';

export const OffboardUserPopupContent = () => (
    <OffboardingProvider>
        <OffboardUserPopupBody />
    </OffboardingProvider>
);

OffboardUserPopupContent.displayName = 'OffboardUserPopupContent';
